import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import {
  LayoutDashboard,
  BarChart3,
  Users,
  MessageSquare,
  CreditCard,
  FileText,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Shield,
  MoreVertical,
  Sparkles,
  ArrowLeft,
  UserCog,
  Eye,
  X,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";

const API = "/api";

// Main navigation items (excluding CRM and Leads - moved to user dropdown)
const adminMenuItems = [
  { path: "/admin", icon: LayoutDashboard, label: "Dashboard" },
  { path: "/admin/analytics", icon: BarChart3, label: "Analytics" },
  { path: "/admin/users", icon: Users, label: "Users" },
  { path: "/admin/conversations", icon: MessageSquare, label: "Conversations" },
  { path: "/admin/payments", icon: CreditCard, label: "Payments" },
  { path: "/admin/team", icon: UserCog, label: "Team" },
  { path: "/admin/files", icon: FileText, label: "Files" },
];

// Product Manager only sees CRM
const productManagerMenuItems = [
  { path: "/admin/crm", icon: Sparkles, label: "CRM" },
];

// Sales role - CRM only (same as Product Manager but with AI Call access)
const salesMenuItems = [
  { path: "/admin/crm", icon: Sparkles, label: "CRM" },
];

// Marketing role - CRM + Campaigns
const marketingMenuItems = [
  { path: "/admin/crm", icon: Sparkles, label: "CRM" },
  { path: "/admin/campaigns", icon: BarChart3, label: "Campaigns" },
];

// Full-screen routes (no sidebar)
const fullScreenRoutes = ["/admin/crm", "/admin/leads"];

// CRM-only roles (no access to full admin panel)
const CRM_ONLY_ROLES = ["product_manager", "sales", "marketing"];

export default function AdminLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [adminEmail, setAdminEmail] = useState("");
  const [userRole, setUserRole] = useState("admin");
  const [userName, setUserName] = useState("");
  const [isEmulating, setIsEmulating] = useState(false);
  const [emulatedUserEmail, setEmulatedUserEmail] = useState("");

  // Check if current route should be full-screen (no sidebar)
  const isFullScreen = fullScreenRoutes.some(route => location.pathname.startsWith(route));

  // Get menu items based on role
  const getMenuItems = () => {
    switch (userRole) {
      case "admin": return adminMenuItems;
      case "marketing": return marketingMenuItems;
      case "sales": return salesMenuItems;
      case "product_manager": 
      default: return productManagerMenuItems;
    }
  };
  const menuItems = getMenuItems();

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    const email = localStorage.getItem("admin_email");
    const role = localStorage.getItem("admin_role") || "admin";
    const name = localStorage.getItem("admin_name") || "";
    
    // Check emulation mode
    const emulationMode = localStorage.getItem("emulation_mode") === "true";
    const emulatedEmail = localStorage.getItem("emulated_user_email");
    setIsEmulating(emulationMode);
    setEmulatedUserEmail(emulatedEmail || "");
    
    if (!token) {
      navigate("/login");
      return;
    }
    
    if (email) {
      setAdminEmail(email);
    }
    setUserRole(role);
    setUserName(name);
    
    // CRM-only roles have restricted access
    if (CRM_ONLY_ROLES.includes(role)) {
      // Marketing can also access campaigns
      const allowedPaths = role === "marketing" 
        ? ["/admin/crm", "/admin/leads", "/admin/campaigns"]
        : ["/admin/crm", "/admin/leads"];
      const isAllowed = allowedPaths.some(p => location.pathname.startsWith(p));
      if (!isAllowed) {
        navigate("/admin/crm");
      }
    }
  }, [navigate, location.pathname]);

  const handleEndEmulation = () => {
    // Restore original admin session
    const originalToken = localStorage.getItem("admin_original_token");
    const originalEmail = localStorage.getItem("admin_original_email");
    
    if (originalToken && originalEmail) {
      localStorage.setItem("admin_token", originalToken);
      localStorage.setItem("admin_email", originalEmail);
      localStorage.setItem("admin_role", "admin");
      localStorage.removeItem("admin_name");
    }
    
    // Clear emulation state
    localStorage.removeItem("emulation_mode");
    localStorage.removeItem("emulated_user_email");
    localStorage.removeItem("emulated_user_type");
    localStorage.removeItem("admin_original_token");
    localStorage.removeItem("admin_original_email");
    
    // Navigate back to admin area
    navigate("/admin/team");
    window.location.reload();
  };

  const handleLogout = () => {
    // Clear all authentication tokens
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_email");
    localStorage.removeItem("admin_role");
    localStorage.removeItem("admin_name");
    localStorage.removeItem("admin_team_member_id");
    localStorage.removeItem("auth_token");
    localStorage.removeItem("user");
    navigate("/login");
  };

  const isActive = (path) => {
    if (path === "/admin") {
      return location.pathname === "/admin";
    }
    return location.pathname.startsWith(path);
  };

  // Full-screen layout for CRM and Lead Management (clean, no floating buttons)
  if (isFullScreen) {
    return (
      <div className="min-h-screen bg-[#09090b]">
        {/* Emulation Banner for full-screen mode */}
        {isEmulating && (
          <div className="fixed top-0 left-0 right-0 z-50 bg-amber-500/90 text-black px-4 py-2 flex items-center justify-between" data-testid="emulation-banner">
            <div className="flex items-center gap-2">
              <Eye className="w-4 h-4" />
              <span className="text-sm font-medium">
                You are emulating <strong>{emulatedUserEmail}</strong>
              </span>
            </div>
            <button
              onClick={handleEndEmulation}
              className="flex items-center gap-1 bg-black/20 hover:bg-black/30 px-3 py-1 rounded text-sm font-medium transition-colors"
              data-testid="end-emulation-btn"
            >
              <X className="w-4 h-4" />
              End Emulation
            </button>
          </div>
        )}
        {/* Full-screen content - no floating buttons, navigation handled by page itself */}
        <main className={`min-h-screen ${isEmulating ? 'pt-10' : ''}`}>
          {children}
        </main>
      </div>
    );
  }

  // Standard layout with sidebar
  return (
    <div className="min-h-screen bg-[#0a0a0a] flex">
      {/* Emulation Banner */}
      {isEmulating && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-amber-500/90 text-black px-4 py-2 flex items-center justify-between" data-testid="emulation-banner">
          <div className="flex items-center gap-2">
            <Eye className="w-4 h-4" />
            <span className="text-sm font-medium">
              You are emulating <strong>{emulatedUserEmail}</strong>
            </span>
          </div>
          <button
            onClick={handleEndEmulation}
            className="flex items-center gap-1 bg-black/20 hover:bg-black/30 px-3 py-1 rounded text-sm font-medium transition-colors"
            data-testid="end-emulation-btn"
          >
            <X className="w-4 h-4" />
            End Emulation
          </button>
        </div>
      )}
      
      {/* Sidebar */}
      <aside
        className={`${
          collapsed ? "w-16" : "w-64"
        } bg-[#050505] border-r border-white/5 flex flex-col transition-all duration-300 fixed h-full z-40 ${isEmulating ? 'mt-10' : ''}`}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-white/5">
          {!collapsed && (
            <div className="flex items-center gap-2">
              <Shield className="w-6 h-6 text-[#14A800]" />
              <span className="text-white font-semibold">Admin Panel</span>
            </div>
          )}
          {collapsed && <Shield className="w-6 h-6 text-[#14A800] mx-auto" />}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="p-1 rounded hover:bg-white/5 text-zinc-400 hover:text-white transition-colors"
            data-testid="toggle-sidebar"
          >
            {collapsed ? (
              <ChevronRight className="w-4 h-4" />
            ) : (
              <ChevronLeft className="w-4 h-4" />
            )}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 px-2 space-y-1 overflow-y-auto">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 group ${
                  active
                    ? "text-[#14A800] bg-[#14A800]/10 border-l-2 border-[#14A800]"
                    : "text-zinc-400 hover:text-white hover:bg-white/5"
                }`}
              >
                <Icon className={`w-5 h-5 flex-shrink-0 ${active ? "text-[#14A800]" : ""}`} />
                {!collapsed && <span>{item.label}</span>}
              </Link>
            );
          })}
        </nav>

        {/* User Section with Dropdown */}
        <div className="border-t border-white/5 p-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className={`w-full flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 transition-colors ${
                  collapsed ? "justify-center" : ""
                }`}
                data-testid="admin-user-menu"
              >
                {/* Avatar */}
                <div className="w-9 h-9 rounded-full bg-[#14A800]/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-[#14A800] text-sm font-medium">
                    {adminEmail ? adminEmail.charAt(0).toUpperCase() : "A"}
                  </span>
                </div>
                
                {!collapsed && (
                  <>
                    <div className="flex-1 text-left min-w-0">
                      <p className="text-xs text-zinc-500 uppercase tracking-wider">Logged in as</p>
                      <p className="text-sm text-zinc-300 truncate">{adminEmail || "Admin"}</p>
                    </div>
                    <MoreVertical className="w-4 h-4 text-zinc-500 flex-shrink-0" />
                  </>
                )}
              </button>
            </DropdownMenuTrigger>
            
            <DropdownMenuContent 
              align={collapsed ? "center" : "end"} 
              side="top"
              className="w-56 bg-[#121212] border-white/10 mb-2"
            >
              {/* Email display in dropdown when collapsed */}
              {collapsed && adminEmail && (
                <>
                  <div className="px-3 py-2">
                    <p className="text-xs text-zinc-500">Logged in as</p>
                    <p className="text-sm text-zinc-300 truncate">{adminEmail}</p>
                  </div>
                  <DropdownMenuSeparator className="bg-white/10" />
                </>
              )}
              
              {/* CRM Link */}
              <DropdownMenuItem 
                onClick={() => navigate("/admin/crm")}
                className="text-[#14A800] hover:text-[#14A800] hover:bg-[#14A800]/10 cursor-pointer"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                AI CRM
                <span className="ml-auto px-1.5 py-0.5 text-[9px] font-bold bg-[#14A800] text-black rounded">NEW</span>
              </DropdownMenuItem>
              
              <DropdownMenuSeparator className="bg-white/10" />
              
              <DropdownMenuItem 
                onClick={() => navigate("/admin/settings")}
                className="text-zinc-300 hover:text-white hover:bg-white/5 cursor-pointer"
              >
                <Settings className="w-4 h-4 mr-2" />
                Settings
              </DropdownMenuItem>
              
              <DropdownMenuSeparator className="bg-white/10" />
              
              <DropdownMenuItem 
                onClick={handleLogout}
                className="text-red-400 hover:text-red-300 hover:bg-red-500/10 cursor-pointer"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main Content */}
      <main
        className={`flex-1 ${collapsed ? "ml-16" : "ml-64"} ${isEmulating ? 'mt-10' : ''} transition-all duration-300`}
      >
        {children}
      </main>
    </div>
  );
}
